import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditDeviceComposer, type EditDeviceFormData } from "@/forms/sensory"
import { EditDeviceLifecycleSections } from "@/components/sensory/devices/EditDeviceLifecycleSections"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { useToast } from "@/contexts/ToastContext"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Cpu, Loader2 } from "lucide-react"

function EditDevicePage() {
  const { deviceNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Fetch the full single-device record — the flat getDevices list has no procurementInfo,
  // so it can't tell us the distributor. getDeviceByNumber returns device + procurementInfo.
  const { data, isLoading } = useApiQuery("getDeviceByNumber", { deviceNumber: Number(deviceNumber) })
  const { data: deviceTypesData } = useApiQuery("getDeviceTypes", {})
  // Full device record (with lifecycle sub-records) for the editable sections.
  const { data: fullDeviceData } = useApiQuery("getDeviceByNumber", {
    deviceNumber: Number(deviceNumber),
  })
  const lifecycleContent = fullDeviceData?.content

  const content = (data as any)?.content
  const device = content?.device
  const procurementInfo = content?.procurementInfo

  // Device type can live on the device or on procurementInfo.
  const deviceTypeNumber: number | undefined =
    procurementInfo?.deviceTypeNumber ?? device?.deviceTypeNumber ?? undefined

  // Map deviceTypeNumber -> deviceTypeManufacturerNumber for prefilling the manufacturer
  const manufacturerMap = new Map<number, number>(
    (deviceTypesData?.content?.deviceTypes || [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((dt: any) => dt.deviceTypeNumber != null && dt.deviceTypeManufacturerNumber != null)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((dt: any) => [dt.deviceTypeNumber as number, dt.deviceTypeManufacturerNumber as number])
  )

  const handleBack = () => navigate({ to: '/sensory/devices' })

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0 overflow-hidden">
        <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Cpu className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Device</CardTitle>
              <CardDescription className="text-base">Update device details</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          {device ? (
            <EditDeviceComposer
              deviceNumber={Number(deviceNumber)}
              initialValues={{
                deviceName: device.deviceName ?? '',
                // Schema accepts the full backend status enum, so pass the real status.
                deviceStatus: (device.deviceStatus ?? undefined) as EditDeviceFormData['deviceStatus'],
                deviceTypeNumber: deviceTypeNumber,
                manufacturerNumber:
                  deviceTypeNumber != null
                    ? manufacturerMap.get(deviceTypeNumber)
                    : undefined,
                // Prefill the profile from the full device record (embedded profile
                // or deviceProfileNumber); undefined until the backend link exists.
                deviceProfileNumber:
                  (lifecycleContent?.device as { deviceProfileNumber?: number } | undefined)?.deviceProfileNumber ??
                  (lifecycleContent as { deviceProfile?: { deviceProfileNumber?: number } } | undefined)?.deviceProfile?.deviceProfileNumber ??
                  undefined,
                // Seed the real distributor so the "Ordered From" toggle defaults correctly.
                distributorNumber: procurementInfo?.deviceDistributorNumber ?? undefined,
              }}
              submitLabel="Save Device"
              showHeader={false}
              onSuccess={handleBack}
              onError={(error) => toast({ title: 'Error', description: `Failed to update device: ${error.message}`, type: 'error' })}
              onCancel={handleBack}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Device not found.</p>
          )}
        </CardContent>
      </Card>

      {/* Editable lifecycle sections (procurement, keys, provisioning, fault, retirement).
          Each saves via its own dedicated upsert endpoint without changing device status. */}
      {device && lifecycleContent ? (
        <EditDeviceLifecycleSections
          deviceNumber={Number(deviceNumber)}
          deviceData={lifecycleContent}
        />
      ) : null}
    </div>
  )
}

export const Route = createFileRoute("/sensory/devices/$deviceNumber_/edit")({
  staticData: {
    title: 'Edit Device',
    isSecured: true,
  },
  component: EditDevicePage,
})
